import { store, setUserInfo } from "../../app/store";
import { ODataResponse, UserModel } from "../../data/entities";
import { UserODataRepo, UserRepo } from "../../data/repo";
import { TokenService } from "../../services";
import { BaseEntityService } from "../BaseEntityService"
import { Role } from "../../models";
import { forceLogout } from "../../utils";

export class UserService extends BaseEntityService<UserModel, UserRepo, UserODataRepo> {

  constructor(){
    super(new UserRepo(), new UserODataRepo());
  }

  static readonly getUserInfo = () => store.getState().authentication.userInfo;

  static readonly getUserToken = () => store.getState().authentication.userToken;

  static readonly getTokenData = () => store.getState().authentication.tokenData;


  static readonly getDisplayName = () => {
    let currentUser = this.getUserInfo();
    return !currentUser ? "" :
      `${currentUser?.firstName ?? currentUser.userName} ${currentUser.lastName ?? ""}`;
  }

  static readonly getId = () => this.getUserInfo()?.id ?? 0;

  static readonly getFirstName = () => this.getUserInfo()?.firstName ?? "";

  static readonly getLastName = () => this.getUserInfo()?.lastName ?? "";

  static readonly getUserName = () => this.getUserInfo()?.userName ?? "";

  static readonly getUserEmail = () => this.getUserInfo()?.userName ?? "";

  static readonly isInRole = (roleName: Role) => {
    const userInfo = this.getUserInfo();
    return !!userInfo && userInfo.roles.some(userRole => userRole.role?.name === roleName);

  }

  static readonly isInRoles = (roleNames: Role[]) => roleNames.some(roleName => this.isInRole(roleName));

  static readonly isSignedIn = () => {
    const { isSignedIn, userToken } = store.getState().authentication;
    return isSignedIn && !!userToken && !TokenService.isTokenExpired();
  }

  static readonly signout = () => forceLogout();

  updateProfile = async (model: UserModel) => {
    const userModel = await this.update(model.id!, model);
    if (!userModel) return false;
    let userInfo = store.getState().authentication.userInfo;
    userInfo = {
      ...userInfo,
      id: userModel.id ?? userInfo?.id ?? 0,
      firstName: userModel.firstName ?? userInfo?.firstName ?? "",
      lastName: userModel.lastName ?? userInfo?.lastName ?? "",
      userName: userModel.userName ?? userInfo?.userName ?? "",
      roles: userInfo?.roles ?? []
    };
    store.dispatch(setUserInfo(userInfo));
    return true;
  }

  getUsers = async (oDataQuery: string): Promise<ODataResponse<UserModel>> => await new UserODataRepo().query(oDataQuery);

  refreshUserInfo = async ()=> {
    try {
      const response = await this.repo.getRefreshUserInfo(); 
       store.dispatch(setUserInfo(response));
      return response;
    } catch (error) {
      console.error("Failed to refresh user info", error);
      throw error;
    }
  }
}

export default UserService;
